// The large, near-square destination tile the dashboard is built from.
//
// The console's landing screen used to be a list of links in a sidebar plus
// four money cards, so "where do I go" was answered by reading nav labels.
// A tile answers it with a target big enough to hit without aiming, an icon
// to recognise it by, one line saying what it is for, and — where the page
// can honestly supply one — a live count so the operator knows whether it
// needs them at all.
//
// `count` is deliberately `number | null | undefined` with three distinct
// renderings. A tile whose count has not loaded shows nothing rather than a
// zero: "0 pending approvals" and "we could not reach the server" must never
// look the same on a screen an operator uses to decide whether to act.

import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { Chip, type Tone } from "./primitives";
import { IconArrowRight } from "./icons";

export interface ActionTileProps {
  to: string;
  label: string;
  description: string;
  icon: ReactNode;
  /** Live count. `undefined` = not loaded / not applicable — renders nothing. */
  count?: number | null;
  countLabel?: string;
  /** Tone for the count chip; ignored when there is no count. */
  tone?: Tone;
  /** Renders the tile visually secondary — used for legacy destinations. */
  muted?: boolean;
  badge?: ReactNode;
}

export function ActionTile({
  to,
  label,
  description,
  icon,
  count,
  countLabel,
  tone = "accent",
  muted = false,
  badge,
}: ActionTileProps) {
  const hasCount = typeof count === "number";
  return (
    <Link
      to={to}
      className="g-tile"
      style={{ opacity: muted ? 0.72 : 1 }}
      aria-label={
        hasCount && countLabel ? `${label} — ${count} ${countLabel}` : label
      }
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "var(--r-md)",
            background: muted ? "var(--bg-inset)" : "var(--accent-bg)",
            border: `1px solid ${muted ? "var(--border-faint)" : "var(--accent-border)"}`,
            color: muted ? "var(--fg-dim)" : "var(--accent)",
            flex: "0 0 auto",
          }}
        >
          {icon}
        </span>
        <span style={{ flex: "1 1 auto" }} />
        {badge}
        {hasCount && (
          <Chip tone={count === 0 ? "neutral" : tone} solid>
            {count}
            {countLabel ? ` ${countLabel}` : ""}
          </Chip>
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 15.5, fontWeight: 640, color: "var(--fg)" }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--fg-dim)", lineHeight: 1.45 }}>
        {description}
      </div>

      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 600,
          color: muted ? "var(--fg-dim)" : "var(--accent)",
        }}
      >
        Open
        <IconArrowRight size={14} />
      </div>
    </Link>
  );
}

/** The tile grid.
 *
 *  `minmax(248px, 1fr)` is tuned to the operator's "3–4 cards per row on
 *  desktop": inside a 1480px canvas less a 248px sidebar and 64px of gutter,
 *  248 yields four columns at desktop, three on a 13" laptop, two on a tablet
 *  and one on a phone — the whole responsive ladder from one number.
 *
 *  `auto-fill` rather than `auto-fit` on purpose: `auto-fit` collapses empty
 *  tracks, so a last row holding one card would stretch it to full width and
 *  break the square rhythm the grid exists to keep. */
export function TileGrid({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(248px, 1fr))",
        gap: 14,
      }}
    >
      {children}
    </div>
  );
}
